/* eslint-disable @typescript-eslint/no-explicit-any */
import { UseClientModelContext } from '@mercadoni/sinister/dist/cjs/client/contexts'
import { trackEvent } from '@mercadoni/sinister/dist/cjs/monitors/services'
import CardProductVerticalv2 from '@mercadoni/sinister/dist/cjs/mystiqueCardProductVerticalv2'
import Loader from '@mercadoni/sinister/dist/cjs/mystiqueLoader'
import ProductCardFullLoad from '@mercadoni/sinister/dist/cjs/productv2ProductCardFullLoad'

import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { UseLoggedContext } from '@mercadoni/sinister/dist/cjs/authentication'
import useContainerWidth from '../../hooksv3/useContainerWidth'

import { validateStockWarning } from '../../utils/validateStockWarning'
import { AdsBannerContainer, ProductsStyles } from './components/styles'

import BannerAds from '@/components/BannerAds'
import {
  beaconAttribute,
  hasOnBasketChangeBeaconAttribute,
  hasSponsored,
  sponsoredTagObj,
} from '../../utils/hasSponsored'

import { useReplacementsContext } from '@/context/ReplacementsContext'
import { useRestrictedAgeContext } from '@/context/RestrictedAgeContext'
import NextLinkWrapper from '@/utils/NextLink'
import { usePathname } from 'next/navigation'

const ProductWithIntersectionalBannersLayout = ({
  searchValue,
  products,
  onProductClick,
  fetchMoreData,
  hasMoreData,
  trackSource,
  copies,
  favoriteService,
  onClickPromoTag,
  onGuessAction,
  sendInteractionEvent,
  listInfo,
  innerRef,
}: any) => {
  const productsContainerRef = useRef<HTMLElement>(null)
  const clientModel = UseClientModelContext()
  const { adsBanners } = clientModel
  const [productsContainerWidth, setProductsContainerWidth] = useState(0)
  const { visualViewport } = useContainerWidth(false)
  const { isLogged } = UseLoggedContext()
  const pathname = usePathname()
  const { onAddProduct } = useReplacementsContext()
  const { onBeforeAddProduct } = useRestrictedAgeContext()

  const clientConfig = {
    ...clientModel,
    stockWarning: validateStockWarning(clientModel),
    sponsoredTag: sponsoredTagObj({ clientConfig: clientModel }),
  }

  const PAGE_TYPE = 'searchIntersectional'
  const {
    rowsBeforeBanner = 1,
    adsPerRow = 1,
    id,
    path,
    size,
    aspectRatio,
  } = adsBanners?.[PAGE_TYPE] || {}

  const elementsPerRow = Math.floor(productsContainerWidth / 197)

  useLayoutEffect(() => {
    const id = setTimeout(() => {
      setProductsContainerWidth(productsContainerRef?.current?.offsetWidth || 0)
    }, 500)

    return () => {
      clearTimeout(id)
    }
  }, [visualViewport])

  useEffect(() => {
    if (
      hasSponsored({ clientConfig }) &&
      productsContainerRef?.current?.children
    ) {
      const beaconTrigger = () => {
        if (typeof document === 'undefined') return
        trackEvent({
          path: pathname,
          type: 'product',
          eventName: 'triggerOnLoadBeacons',
        })
        trackEvent({
          path: pathname,
          type: 'product',
          eventName: 'attachOnViewBeacons',
        })

        const onloadbeaconElems = document.querySelectorAll(
          `[data-criteo-placement-onloadbeacon]`
        )

        const onviewbeaconElems = document.querySelectorAll(
          `[data-criteo-placement-onviewbeacon]`
        )

        if (onloadbeaconElems.length > 0) {
          trackEvent({
            path: pathname,
            type: 'placement',
            eventName: 'triggerOnLoadBeacons',
          })
        }

        if (onviewbeaconElems.length > 0) {
          trackEvent({
            path: pathname,
            type: 'placement',
            eventName: 'attachOnViewBeacons',
          })
        }
      }

      setTimeout(beaconTrigger, 2000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsContainerRef])

  const content = products.map((product: any, index: number) => {
    let renderBanner = null

    const onClickAddProduct = () => {
      onAddProduct({ product })
    }

    if ((index + 1) % (elementsPerRow * rowsBeforeBanner) === 0) {
      const bannerNumber = (index + 1) / (elementsPerRow * rowsBeforeBanner)
      renderBanner = (
        <AdsBannerContainer elementNumber={adsPerRow}>
          <Suspense fallback="">
            <BannerAds
              id={`${id}-section-${bannerNumber}`}
              path={path}
              size={size}
              aspectRatio={aspectRatio}
              pagetype={PAGE_TYPE}
              searchterm={searchValue.text}
              showLoad={false}
              adsPerRow={adsPerRow}
              extraTargeting={{
                pos: ['carousel', bannerNumber],
              }}
            />
          </Suspense>
        </AdsBannerContainer>
      )
    }

    const onInteraction = (interaction: any) => {
      if (
        hasSponsored({ clientConfig }) &&
        interaction.interactionName === 'Agregar' &&
        hasOnBasketChangeBeaconAttribute(product)
      ) {
        trackEvent({
          path: pathname,
          type: 'product',
          eventName: 'triggerOnBasketChangeBeacon',
          data: product?.promoted?.onBasketChangeBeacon,
        })
      }
      sendInteractionEvent(interaction)
    }

    return (
      <Suspense
        key={`product-${product.sku}`}
        fallback={<CardProductVerticalv2 loading />}
      >
        {renderBanner}
        <div {...beaconAttribute(product)}>
          <ProductCardFullLoad
            product={product}
            trackSource={trackSource}
            onClick={onProductClick}
            clientConfig={clientConfig}
            favoriteService={favoriteService}
            onInteraction={onInteraction}
            listInfo={listInfo}
            copies={copies}
            index={index}
            onClickPromoTag={onClickPromoTag}
            onCarlessProduct={onGuessAction}
            onClickAddProduct={onClickAddProduct}
            isLogged={isLogged}
            onBeforeAddProduct={onBeforeAddProduct}
            LinkComponent={NextLinkWrapper}
          />
        </div>
      </Suspense>
    )
  })

  return (
    <>
      <InfiniteScroll
        ref={innerRef}
        dataLength={products.length}
        next={fetchMoreData}
        hasMore={hasMoreData}
        loader={<Loader />}
        scrollThreshold={0.6}
      >
        <ProductsStyles ref={productsContainerRef}>{content}</ProductsStyles>
      </InfiniteScroll>
    </>
  )
}

export default ProductWithIntersectionalBannersLayout
